import { apiCall } from "@/api/client";
import { toSnakeCase } from "@/api/payloadCase";
import type {
  CatalogHealth,
  ComponentUsage,
  GatewayComponent,
} from "@/types/api";

/**
 * The backend camelizes its responses recursively via `json_response`, but
 * `body` (the component's docker block) and `capability` are opaque blobs
 * consumed in snake_case by the Rust dataplane. We re-normalize them
 * symmetrically on read and write — the same contract
 * `normaliseItemPayload` applies to `payload` in api/catalog.ts. Breaking
 * that symmetry is exactly what left four catalog types unusable: the Rust
 * side looked up `components_catalog` and got `componentsCatalog` instead.
 */
function normalise(component: GatewayComponent): GatewayComponent {
  return {
    ...component,
    body: toSnakeCase(component.body),
    capability: component.capability
      ? toSnakeCase(component.capability)
      : null,
  };
}

function componentPath(group: string, name: string): string {
  return `/catalog/gateways/components/${encodeURIComponent(group)}/${encodeURIComponent(name)}`;
}

export async function listComponents(token: string) {
  const res = await apiCall<{ items: GatewayComponent[] }>(
    "/catalog/gateways/components",
    { token },
  );
  return { items: res.items.map(normalise) };
}

export async function getComponent(
  token: string,
  group: string,
  name: string,
) {
  return normalise(
    await apiCall<GatewayComponent>(componentPath(group, name), { token }),
  );
}

export async function createComponent(
  token: string,
  // ``source``/``pendingImport`` are system fields no admin write ever sets
  // — the backend strips them from this route's body regardless (see
  // ``_component_from_body`` in handler.py) — so the write payload's type
  // excludes them rather than asking every caller to fill in a value that
  // would be ignored.
  component: Omit<GatewayComponent, "updatedAt" | "source" | "pendingImport">,
) {
  return normalise(
    await apiCall<GatewayComponent>(
      componentPath(component.group, component.name),
      {
        method: "POST",
        body: {
          body: toSnakeCase(component.body),
          capability: component.capability
            ? toSnakeCase(component.capability)
            : null,
          capability_key: component.capabilityKey,
          inclusion: component.inclusion,
          params: component.params,
        },
        token,
      },
    ),
  );
}

/**
 * ``confirmImpactedTypes`` acknowledges an "always included" → "optional"
 * switch: that edit removes the component from every gateway type that
 * ships it today without any recipe changing, and nothing downstream fails
 * — the backend refuses it (409) unless the caller echoes back the exact
 * number of impacted types it was shown. A stale number is refused too:
 * the operator would then be confirming a scope that no longer exists.
 */
export async function updateComponent(
  token: string,
  // Same rationale as ``createComponent`` above: ``source``/``pendingImport``
  // are read-only on this route (the backend carries the stored values
  // forward regardless of what the body says), so the write payload never
  // needs to supply them.
  component: Omit<GatewayComponent, "source" | "pendingImport">,
  confirmImpactedTypes?: number,
) {
  return normalise(
    await apiCall<GatewayComponent>(
      componentPath(component.group, component.name),
      {
        method: "PUT",
        body: {
          body: toSnakeCase(component.body),
          capability: component.capability
            ? toSnakeCase(component.capability)
            : null,
          capability_key: component.capabilityKey,
          inclusion: component.inclusion,
          params: component.params,
          // Optimistic lock token: without it the backend refuses with 400.
          updated_at: component.updatedAt,
          ...(confirmImpactedTypes === undefined
            ? {}
            : { confirm_impacted_types: confirmImpactedTypes }),
        },
        token,
      },
    ),
  );
}

/**
 * POST .../pending-import/accept — applies the import's pending ``inclusion``
 * and/or ``mandatory`` and clears ``pendingImport``. Empty body: the
 * optimistic-lock token is re-read on the brick server-side, not supplied
 * here — see the route's docstring in ``lambdas/catalog_admin/handler.py``.
 */
export async function acceptPendingImport(
  token: string,
  group: string,
  name: string,
) {
  return normalise(
    await apiCall<GatewayComponent>(
      `${componentPath(group, name)}/pending-import/accept`,
      { method: "POST", token },
    ),
  );
}

export function deleteComponent(token: string, group: string, name: string) {
  return apiCall<{ deleted: boolean }>(componentPath(group, name), {
    method: "DELETE",
    token,
  });
}

export function getComponentUsage(
  token: string,
  group: string,
  name: string,
) {
  return apiCall<ComponentUsage>(`${componentPath(group, name)}/usage`, {
    token,
  });
}

/**
 * Un maillon de la chaîne de livrabilité — cinq par stage, dans l'ordre
 * rendu par l'API (``LINKS_IN_ORDER`` dans component_status.py) : cet ordre
 * porte la causalité (un maillon ne peut pas être vert si le précédent est
 * rouge) et ne doit jamais être réordonné côté client.
 */
export type ComponentLink = {
  link: string;
  ok: boolean;
  /**
   * Faux quand le maillon est SANS OBJET pour cette brique — un widget
   * frontend n'a ni dépôt, ni image, ni grants. ``ok`` reste vrai (il ne
   * rend pas la brique non livrable), mais la cellule ne doit pas se donner
   * pour vérifiée : gris, jamais vert.
   */
  applicable: boolean;
  detail: string;
  remedy: string | null;
};

export type ComponentStageStatus = {
  tenant: string;
  stage: string;
  deliverable: boolean;
  links: ComponentLink[];
};

export type ComponentStatus = {
  componentRef: string;
  stages: ComponentStageStatus[];
};

/**
 * GET .../status — la livrabilité d'un composant, stage par stage. Pas de
 * ``normalise`` ici : contrairement à ``body``/``capability`` (des blobs
 * opaques consommés en snake_case par le dataplane Rust), cette réponse est
 * une donnée déclarative que le handler sérialise via ``json_response`` —
 * camelCase par défaut (vérifié dans ``_component_status``,
 * lambdas/catalog_admin/handler.py). Rien à reconvertir.
 */
export function getComponentStatus(token: string, group: string, name: string) {
  return apiCall<ComponentStatus>(`${componentPath(group, name)}/status`, {
    token,
  });
}

export function listImageRepositories(token: string) {
  return apiCall<{ items: string[] }>(
    "/catalog/gateways/image-repositories",
    { token },
  );
}

export function getCatalogHealth(token: string) {
  return apiCall<CatalogHealth>("/catalog/gateways/health", { token });
}

export async function getComposedPayload(
  token: string,
  model: string,
  version: string,
) {
  const res = await apiCall<{ payload: Record<string, unknown> }>(
    `/catalog/gateways/instances/customer/${encodeURIComponent(model)}/${encodeURIComponent(version)}/composed`,
    { token },
  );
  return toSnakeCase(res.payload);
}
